/**
 * FootnoteSuperscript
 *
 * The single DOM renderer for footnote ordinal markers across the deck grid. Footnotes are
 * `[Int]` ordinal arrays that the backend attaches at every level — deckline (KPI name),
 * header-tree GroupNode/BlockNode/LeafNode/ColumnLeaf, and PageLayout (with cell-level soon) —
 * all pointing into the page's Footnote definitions. Every DOM consumer renders them the same
 * way: comma-joined ordinals in a single superscript. This component is that one renderer, so
 * the KPI-name column, headerTree headers, and future cell markers can't drift.
 *
 * For NON-DOM contexts (Excel export, AG Grid string values) use the sibling
 * `convertFootnoteNumberToSuperscript` in `@utils/footnoteHelpers`, which renders the same
 * ordinals as Unicode superscript glyphs. Same footnote data, two rendering targets.
 */

import { KeyboardEvent, MouseEvent } from 'react';

/**
 * Props for {@link FootnoteSuperscript}.
 */
export interface FootnoteSuperscriptProps {
  /**
   * Footnote ordinals attached to the element (`[Int]` from the API — deckline, header node,
   * columnLeaf, or page). Null entries are filtered; empty/absent renders nothing.
   */
  footnotes?: (number | null)[] | null;
  /**
   * Optional click handler. When provided, the superscript becomes an interactive button (used by
   * column headers to drive the footnotes pane). When omitted, the marker is plain static text —
   * KPI-name cells and other read-only usages are unaffected.
   */
  onClick?: () => void;
}

/**
 * Renders footnote ordinals as a single comma-joined superscript (e.g. `¹` → `<sup>1,2</sup>`),
 * or `null` when there are no footnotes. Mirrors the legacy KPI-name footnote output and adds a
 * WCAG `aria-label` (NFR5) so screen readers announce the markers. When `onClick` is supplied the
 * same marker is exposed as a keyboard-accessible button.
 */
const FootnoteSuperscript = ({ footnotes, onClick }: FootnoteSuperscriptProps) => {
  const footnotesString = (footnotes ?? []).filter((footnote) => footnote != null).join(',');

  if (footnotesString.length === 0) {
    return null;
  }

  // Interactive attributes are added only when a handler is supplied, so read-only usages
  // (KPI-name cells, plain headers) stay non-interactive. The <sup> content/aria-label are shared.
  const interactiveProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        className: 'footnote-superscript-clickable',
        onClick: (event: MouseEvent) => {
          // Stop the click from bubbling to AG Grid's header (sort/menu) handlers.
          event.stopPropagation();
          onClick();
        },
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            onClick();
          }
        },
      }
    : {};

  return (
    <sup aria-label={`Footnotes ${footnotesString}`} {...interactiveProps}>
      {footnotesString}
    </sup>
  );
};

export default FootnoteSuperscript;
